'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import AdvertisePopup from '@/components/ads/AdvertisePopup';
interface InterstitialAdProps {
  mobileCode?: string;
  desktopCode?: string;
  index: number;
  /** When true, adds col-span-full class for grid layouts */
  fullWidth?: boolean;
}

/**
 * Generate a 6-character random alphanumeric code
 */
function generateUniqueCode(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Replace div IDs in ad code with unique IDs to prevent conflicts
 * Finds all div-gpt-ad-XXXXX-X patterns and appends unique code
 */
function makeAdCodeUnique(code: string, uniqueCode: string): string {
  if (!code) return '';

  // Find all unique div-gpt-ad IDs in the code
  const divIdPattern = /div-gpt-ad-[\d]+-[\d]+/g;
  const foundIds = new Set<string>();
  let match;
  while ((match = divIdPattern.exec(code)) !== null) {
    foundIds.add(match[0]);
  }

  // Replace each unique ID with a new unique version
  let result = code;
  foundIds.forEach(originalId => {
    const newId = `${originalId}-${uniqueCode}`;
    // Replace all occurrences of this ID (in id attributes, display calls, defineSlot, etc.)
    result = result.split(originalId).join(newId);
  });
  return result;
}

/**
 * Extract ad slot size from GPT ad code (defineSlot call).
 * Returns { width, height } or defaults to { width: 300, height: 250 }.
 */
function extractAdSize(adCode: string): {
  width: number;
  height: number;
} {
  if (!adCode) return {
    width: 300,
    height: 250
  };
  const match = adCode.match(/defineSlot\(\s*['"][^'"]+['"]\s*,\s*(\[[^\]]+\])\s*,/);
  if (!match) return {
    width: 300,
    height: 250
  };
  try {
    const sizes = JSON.parse(match[1]);
    if (Array.isArray(sizes[0])) {
      return {
        width: sizes[0][0],
        height: sizes[0][1]
      };
    }
    if (typeof sizes[0] === 'number' && typeof sizes[1] === 'number') {
      return {
        width: sizes[0],
        height: sizes[1]
      };
    }
  } catch {/* ignore */}
  return {
    width: 300,
    height: 250
  };
}

/**
 * Extract non-script HTML and scripts separately from ad code
 */
function parseAdCode(code: string): {
  html: string;
  scripts: {
    src?: string;
    content?: string;
  }[];
} {
  if (!code) return {
    html: '',
    scripts: []
  };
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = code;
  const scripts: {
    src?: string;
    content?: string;
  }[] = [];
  const scriptElements = tempDiv.querySelectorAll('script');
  scriptElements.forEach(script => {
    if (script.src) {
      scripts.push({
        src: script.src
      });
    } else if (script.textContent) {
      scripts.push({
        content: script.textContent
      });
    }
    script.remove();
  });
  return {
    html: tempDiv.innerHTML,
    scripts
  };
}
export default function InterstitialAd({
  mobileCode,
  desktopCode,
  index,
  fullWidth = false
}: InterstitialAdProps) {
  const uniqueCode = useMemo(() => generateUniqueCode(), []);
  const mobileRef = useRef<HTMLDivElement | null>(null);
  const desktopRef = useRef<HTMLDivElement | null>(null);
  const executedRef = useRef(false);
  const [isClient, setIsClient] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  // Check if ad codes are valid
  const hasMobileCode = !!(mobileCode && mobileCode.trim().length > 0);
  const hasDesktopCode = !!(desktopCode && desktopCode.trim().length > 0);

  // Extract fixed ad sizes from ad codes
  const mobileAdSize = useMemo(() => hasMobileCode ? extractAdSize(mobileCode!) : {
    width: 300,
    height: 250
  }, [mobileCode, hasMobileCode]);
  const desktopAdSize = useMemo(() => hasDesktopCode ? extractAdSize(desktopCode!) : {
    width: 300,
    height: 250
  }, [desktopCode, hasDesktopCode]);

  // Early return for SSR - don't render anything if no valid codes at all
  // This check happens before any hooks that depend on browser APIs
  const hasAnyValidCode = hasMobileCode || hasDesktopCode;

  // Process ad codes to make IDs unique
  const processedMobileCode = useMemo(() => hasMobileCode ? makeAdCodeUnique(mobileCode!, `${uniqueCode}_m${index}`) : '', [mobileCode, hasMobileCode, uniqueCode, index]);
  const processedDesktopCode = useMemo(() => hasDesktopCode ? makeAdCodeUnique(desktopCode!, `${uniqueCode}_d${index}`) : '', [desktopCode, hasDesktopCode, uniqueCode, index]);

  // Determine viewport on client side only
  useEffect(() => {
    const checkViewport = () => {
      const isMobile = window.innerWidth < 768;
      setIsMobileView(isMobile);
    };
    setIsClient(true);
    checkViewport();

    // Listen for resize to handle viewport changes
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // Calculate if we should render based on viewport and available codes
  const shouldRender = isClient && hasAnyValidCode && (isMobileView && hasMobileCode || !isMobileView && hasDesktopCode);

  // Execute ad scripts after component mounts and viewport is determined.
  // Deferred to idle time — this ad is part of priority (above-the-fold)
  // content, so it hydrates in the same burst as the rest of the page. Running
  // its DOM mutation + GPT script load synchronously in that burst competes
  // with hydration for the main thread and can show up as a brief flicker
  // right as the ad slot appears.
  useEffect(() => {
    if (!isClient || !shouldRender || executedRef.current) return;
    executedRef.current = true;
    const runIdle: (cb: () => void) => void = typeof window.requestIdleCallback === 'function' ? cb => window.requestIdleCallback(cb, {
      timeout: 1000
    }) : cb_0 => setTimeout(cb_0, 0);
    const executeAdScripts = (code: string, container: HTMLDivElement | null) => {
      if (!code || !container) return;
      const {
        html,
        scripts
      } = parseAdCode(code);

      // Set HTML content
      container.innerHTML = html;

      // Execute scripts
      scripts.forEach(scriptInfo => {
        try {
          if (scriptInfo.src) {
            // Check if GPT script is already loaded
            if (scriptInfo.src.includes('gpt.js') && (window as any).googletag) {
              return; // Skip loading GPT again
            }
            const newScript = document.createElement('script');
            newScript.src = scriptInfo.src;
            newScript.async = true;
            document.head.appendChild(newScript);
          } else if (scriptInfo.content) {
            // Execute inline script
            try {
              const fn = new Function(scriptInfo.content);
              fn();
            } catch {
              // Fallback: create script element
              const newScript_0 = document.createElement('script');
              newScript_0.textContent = scriptInfo.content;
              document.head.appendChild(newScript_0);
              newScript_0.remove();
            }
          }
        } catch (err) {
          console.error('[InterstitialAd] Script execution error:', err);
        }
      });
    };

    // Execute based on viewport, off the hydration critical path
    runIdle(() => {
      if (isMobileView && hasMobileCode && processedMobileCode) {
        executeAdScripts(processedMobileCode, mobileRef.current);
      } else if (!isMobileView && hasDesktopCode && processedDesktopCode) {
        executeAdScripts(processedDesktopCode, desktopRef.current);
      }
    });
  }, [isClient, shouldRender, isMobileView, hasMobileCode, hasDesktopCode, processedMobileCode, processedDesktopCode]);

  // Skip entirely if no codes at all (nothing to ever render)
  if (!hasAnyValidCode) {
    return null;
  }

  // Render BOTH placeholders during SSR with CSS media queries gating
  // visibility — this guarantees the space is reserved on first paint, and
  // the viewport-correct slot is the only one actually visible/measurable.
  // Width/height come from the ad code so the box matches the served creative.
  // Hide the WHOLE container (gray padded box + advertise popup) on viewports
  // that have no ad code. If we only hide the inner ref div, the outer wrapper
  // still reserves vertical space and looks like an empty grey slot.
  //   - mobile-only ad   → `md:hidden`   (hide entire wrapper on desktop)
  //   - desktop-only ad  → `hidden md:block` (hide entire wrapper on mobile)
  //   - both             → no visibility class (rendered on both)
  const wrapperVisibilityClass = hasMobileCode && !hasDesktopCode ? 'md:hidden' : !hasMobileCode && hasDesktopCode ? 'hidden md:block' : '';
  return <div className={`interstitial-ad-container my-2${fullWidth ? ' col-span-full' : ''}${wrapperVisibilityClass ? ' ' + wrapperVisibilityClass : ''}`}>
      <div className="bg-gray-100 p-[5px] relative">
        <div className="flex justify-center">
          {hasMobileCode && <div ref={mobileRef} className="flex items-center justify-center md:hidden" style={{
          width: mobileAdSize.width,
          height: mobileAdSize.height
        }} data-ad-type="mobile" data-ad-index={index} />}

          {hasDesktopCode && <div ref={desktopRef} className="hidden md:flex items-center justify-center" style={{
          width: desktopAdSize.width,
          height: desktopAdSize.height
        }} data-ad-type="desktop" data-ad-index={index} />}
        </div>

        <AdvertisePopup />
      </div>
    </div>;
}